import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { IconBadge } from '../components/icons/ServiceIcons';
import './ServicePage.css';
const dataScienceArtwork = '/assets/illustrations/data-science-insights.svg';

const DataScience = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [processRef, processInView] = useInView({ threshold: 0.2, triggerOnce: true });

  const features = [
    {
      icon: 'forecast',
      title: 'Predictive Analytics',
      description: 'Forecast demand, attrition, and financial outcomes with machine learning models tuned to your business signals.'
    },
    {
      icon: 'analytics',
      title: 'Statistical Analysis',
      description: 'Hypothesis testing, experimental design, and causal inference that deliver defensible insights for leadership decisions.'
    },
    {
      icon: 'brain',
      title: 'Machine Learning',
      description: 'Custom ML pipelines for classification, regression, clustering, and recommendations, engineered for production reliability.'
    },
    {
      icon: 'dashboard',
      title: 'Business Intelligence',
      description: 'Interactive dashboards and data stories that align KPIs, executive narratives, and operational follow-through.'
    },
    {
      icon: 'target',
      title: 'Customer Analytics',
      description: 'Segmentation, lifetime value, and propensity modelling to sharpen marketing investment and retention programmes.'
    },
    {
      icon: 'realtime',
      title: 'Real-time Analytics',
      description: 'Streaming analytics and event-driven architectures that trigger decisions in milliseconds with full governance.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Data Discovery & Assessment',
      description: 'Evaluate your data landscape, catalog sources, and baseline quality to prioritise high-value analytics initiatives.',
      icon: 'assessment'
    },
    {
      step: '02',
      title: 'Problem Definition',
      description: 'Partner with stakeholders to set value hypotheses, success metrics, and governance requirements for each use case.',
      icon: 'target'
    },
    {
      step: '03',
      title: 'Data Preparation',
      description: 'Cleanse, engineer, and enrich datasets with reproducible pipelines, documentation, and data contracts.',
      icon: 'dataStack'
    },
    {
      step: '04',
      title: 'Model Development',
      description: 'Develop and validate statistical and ML models using robust experimentation frameworks and model explainability checks.',
      icon: 'brain'
    },
    {
      step: '05',
      title: 'Deployment & Monitoring',
      description: 'Operationalise models with CI/CD, bias monitoring, and adaptive retraining, ensuring every insight stays production-ready.',
      icon: 'analytics'
    }
  ];

  const technologies = [
    {
      name: 'Python',
      tagline: 'Core Language',
      description: 'Fast prototyping, ETL scripting, and ML research supported by rich open-source libraries.',
      icon: 'code'
    },
    {
      name: 'R',
      tagline: 'Statistical Computing',
      description: 'Advanced analytics, forecasting, and reporting for highly regulated workloads.',
      icon: 'code'
    },
    {
      name: 'SQL & dbt',
      tagline: 'Data Marts',
      description: 'Transformations, testing, and lineage to power governed semantic layers.',
      icon: 'dataStack'
    },
    {
      name: 'TensorFlow & Keras',
      tagline: 'Deep Learning',
      description: 'Neural network development for computer vision, NLP, and forecasting.',
      icon: 'brain'
    },
    {
      name: 'PyTorch & Lightning',
      tagline: 'Model Research',
      description: 'Flexible experimentation framework for state-of-the-art AI initiatives.',
      icon: 'brain'
    },
    {
      name: 'Scikit-learn & XGBoost',
      tagline: 'Classical ML',
      description: 'Ensemble modelling, feature engineering, and auto-ML workflows.',
      icon: 'analytics'
    },
    {
      name: 'Pandas, NumPy & Polars',
      tagline: 'Feature Engineering',
      description: 'High-performance tabular processing for pipelines and exploratory analysis.',
      icon: 'layers'
    },
    {
      name: 'Apache Spark & Databricks',
      tagline: 'Distributed Processing',
      description: 'Large-scale ETL, MLflow experiment tracking, and collaborative notebooks.',
      icon: 'spark'
    },
    {
      name: 'Snowflake & BigQuery',
      tagline: 'Cloud Data Platforms',
      description: 'Elastic compute and secure data sharing for analytics and AI workloads.',
      icon: 'dataStack'
    },
    {
      name: 'Azure ML & SageMaker',
      tagline: 'Model Operations',
      description: 'Managed training, deployment, and monitoring pipelines with built-in governance.',
      icon: 'cloudUpload'
    },
    {
      name: 'Tableau & Power BI',
      tagline: 'Business Intelligence',
      description: 'Interactive storytelling, KPI dashboards, and executive-ready visuals.',
      icon: 'dashboard'
    },
    {
      name: 'Alteryx & Dataiku',
      tagline: 'Citizen Data Science',
      description: 'Low-code workflows enabling analyst teams to contribute to ML initiatives.',
      icon: 'insight'
    }
  ];

  const marqueeRows = [
    technologies.filter((_, index) => index % 2 === 0),
    technologies.filter((_, index) => index % 2 === 1)
  ].filter((row) => row.length);

  const useCases = [
    {
      title: 'Fraud Detection',
      description: 'Identify anomalous patterns across payments, claims, and user activity with adaptive monitoring and human review loops.',
      icon: 'shield'
    },
    {
      title: 'Demand Forecasting',
      description: 'Predict volume and mix with multivariate forecasting that aligns supply chain, staffing, and revenue planning.',
      icon: 'forecast'
    },
    {
      title: 'Recommendation Systems',
      description: 'Deliver personalised content and product experiences powered by behavioural signals and contextual metadata.',
      icon: 'target'
    },
    {
      title: 'Price Optimisation',
      description: 'Model elasticity, competitor dynamics, and margin impacts to dynamically tune pricing and promotion strategies.',
      icon: 'savings'
    }
  ];

  return (
    <motion.div 
      className="service-page data-science-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Hero Section */}
      <section className="service-hero parallax-container" ref={heroRef}>
        <div className="parallax-bg"></div>
        <div className="container">
          <motion.div 
            className="hero-content text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div 
              className="hero-icon"
              initial={{ scale: 0 }}
              animate={heroInView ? { scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <img 
                src={dataScienceArtwork} 
                alt="Abstract analytics illustration" 
              />
            </motion.div>
            <h1>Data Science Solutions</h1>
            <p className="hero-description">
              Unlock the hidden value in your data estate with advisory, engineering, and operational support that takes models from discovery to scaled adoption. We ensure every insight is traceable, governed, and aligned to meaningful business outcomes.
            </p>
            <Link to="/contact" className="btn">Unlock Your Data's Potential</Link>
          </motion.div>
        </div>
      </section>

      {/* What We Do */}
      <section className="what-we-do section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What We Do</h2>
            <p>
              Consultants, scientists, and visual storytellers collaborate to take use cases from hypothesis through adoption. We operationalise governance, accelerate experimentation, and ensure every deliverable is packaged for decision-makers and engineering teams alike.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section section section-alt" ref={featuresRef}>
        <div className="container">
          <div className="grid-3">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-card card"
                initial={{ opacity: 0, y: 50 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="feature-icon">
                  <IconBadge name={feature.icon} size={72} backgroundOpacity={0.24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section" ref={processRef}>
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={processInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Data Science Methodology</h2>
            <p>A proven approach to extracting value from your data assets</p>
          </motion.div>
          
          <div className="process-timeline">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                className="process-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={processInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="process-number">{item.step}</div>
                <div className="process-content">
                  <div className="process-icon">
                    <IconBadge
                      name={item.icon}
                      className="icon-badge--sm"
                      size={58}
                      backgroundOpacity={0.24}
                    />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases-section section section-alt">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Data Science Use Cases</h2>
            <p>Real-world applications that drive business value</p>
          </motion.div>
          
          <div className="grid-4">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                className="use-case-item card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
                viewport={{ once: true }}
              >
                <div className="use-case-icon">
                  <IconBadge
                    name={useCase.icon}
                    className="icon-badge--sm"
                    size={56}
                    backgroundOpacity={0.24}
                  />
                </div>
                <h3>{useCase.title}</h3>
                <p>{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="technologies-section section">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Technologies & Tools</h2>
            <p>Cutting-edge data science and analytics technologies</p>
          </motion.div>
          
          <motion.div
            className="tech-marquee-wrapper"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {marqueeRows.map((row, rowIndex) => (
              <div
                className={`tech-marquee${rowIndex % 2 === 1 ? ' tech-marquee--reverse' : ''}`}
                key={`datascience-row-${rowIndex}`}
              >
                <div className="tech-marquee__track">
                  {[...row, ...row].map((tech, index) => (
                    <div className="tech-marquee__card" key={`datascience-tech-${rowIndex}-${index}-${tech.name}`}>
                      <IconBadge
                        name={tech.icon}
                        className="tech-marquee__badge icon-badge--sm"
                        size={58}
                        backgroundOpacity={0.24}
                      />
                      <div className="tech-marquee__label">
                        <span className="tech-marquee__title">{tech.name}</span>
                        {tech.tagline && (
                          <span className="tech-marquee__subtitle">{tech.tagline}</span>
                        )}
                        {tech.description && (
                          <span className="tech-marquee__description">{tech.description}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default DataScience;
